'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';

const CONSENT_KEY = 'cookie_consent';

export type CookieConsentValue = 'accepted' | 'rejected' | 'custom';

export interface CookieConsentData {
  value: CookieConsentValue;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: string;
}

export function getStoredCookieConsent(): CookieConsentData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsentData;
  } catch {
    return null;
  }
}

export function storeCookieConsent(data: CookieConsentData): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
  const maxAge = 365 * 24 * 60 * 60;
  document.cookie = `${CONSENT_KEY}=${encodeURIComponent(JSON.stringify(data))}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

interface CustomPreferencesProps {
  onSave: (data: CookieConsentData) => void;
  onClose: () => void;
}

function CustomPreferences({ onSave, onClose }: CustomPreferencesProps) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [preferences, setPreferences] = useState(false);

  const handleSave = () => {
    onSave({ value: 'custom', analytics, marketing, preferences, timestamp: new Date().toISOString() });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 p-6 shadow-xl space-y-5 max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          {t('cookieConsent.customizeTitle', 'Preferinte cookie')}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('cookieConsent.customizeDesc', 'Gestionati ce tipuri de cookie acceptati. Preferintele pot fi modificate oricand.')}
        </p>

        <div className="flex items-start gap-3 p-3 rounded-md bg-slate-50 dark:bg-slate-700">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('cookieConsent.required', 'Necesare')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('cookieConsent.requiredDesc', 'Autentificare, sesiune, CSRF, preferinte de limba. Nu pot fi dezactivate.')}
            </p>
          </div>
          <span className="shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {t('cookieConsent.alwaysOn', 'Mereu activ')}
          </span>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-md border dark:border-slate-600">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('cookieConsent.analytics', 'Analiza')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('cookieConsent.analyticsDesc', 'Statistici de vizitare anonimizate. Ajuta la imbunatatirea platformei.')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={analytics}
            aria-label={t('cookieConsent.analytics', 'Analiza')}
            onClick={() => setAnalytics((v) => !v)}
            className={`shrink-0 mt-0.5 relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
              analytics ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
                analytics ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-md border dark:border-slate-600">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('cookieConsent.marketing', 'Marketing')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('cookieConsent.marketingDesc', 'Urmarire campanii, parametri UTM. Folosit pentru masurarea eficientei campaniilor.')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={marketing}
            aria-label={t('cookieConsent.marketing', 'Marketing')}
            onClick={() => setMarketing((v) => !v)}
            className={`shrink-0 mt-0.5 relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
              marketing ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
                marketing ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-md border dark:border-slate-600">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('cookieConsent.preferences', 'Preferinte')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('cookieConsent.preferencesDesc', 'Retine preferintele de interfata: tema, limba, layout. Nu identifica utilizatorul.')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={preferences}
            aria-label={t('cookieConsent.preferences', 'Preferinte')}
            onClick={() => setPreferences((v) => !v)}
            className={`shrink-0 mt-0.5 relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
              preferences ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
                preferences ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 rounded-md bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-sm font-medium text-white"
          >
            {t('cookieConsent.savePreferences', 'Salveaza preferintele')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            {t('cookieConsent.cancel', 'Anulează')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (!getStoredCookieConsent()) {
      setVisible(true);
    }
  }, []);

  const acceptAll = () => {
    storeCookieConsent({ value: 'accepted', analytics: true, marketing: true, preferences: true, timestamp: new Date().toISOString() });
    setVisible(false);
  };

  const rejectAll = () => {
    storeCookieConsent({ value: 'rejected', analytics: false, marketing: false, preferences: false, timestamp: new Date().toISOString() });
    setVisible(false);
  };

  const saveCustom = (data: CookieConsentData) => {
    storeCookieConsent(data);
    setShowCustom(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {showCustom && (
        <CustomPreferences onSave={saveCustom} onClose={() => setShowCustom(false)} />
      )}
      <div
        role="dialog"
        aria-label={t('cookieConsent.bannerLabel', 'Consimtamant cookie')}
        className="fixed bottom-0 inset-x-0 bg-slate-900 text-white p-4 z-50 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-lg"
      >
        <p className="flex-1 text-sm">
          {t('cookieConsent.bannerText', 'Folosim cookie-uri pentru a va oferi cea mai buna experienta. Cookie-urile necesare sunt intotdeauna active.')}{' '}
          <a href="/privacy-policy" className="underline hover:text-slate-300">
            {t('cookieConsent.privacyLink', 'Politica de confidentialitate')}
          </a>
        </p>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            className="text-sm font-medium px-3 py-1.5 rounded border border-slate-500 hover:border-slate-300 text-slate-300 hover:text-white"
            onClick={() => setShowCustom(true)}
          >
            {t('cookieConsent.customize', 'Personalizeaza')}
          </button>
          <button
            type="button"
            className="bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium px-4 py-1.5 rounded"
            onClick={rejectAll}
          >
            {t('cookieConsent.rejectOptional', 'Refuza optionale')}
          </button>
          <button
            type="button"
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-1.5 rounded"
            onClick={acceptAll}
          >
            {t('cookieConsent.acceptAll', 'Accept tot')}
          </button>
        </div>
      </div>
    </>
  );
}
